import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Avatar,
  LinearProgress,
  Divider,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import EmergencyChronometer from '../components/EmergencyChronometer';
import {
  AirportShuttle as AmbulanceIcon,
  Navigation as NavigationIcon,
  Phone as PhoneIcon,
  Warning as EmergencyIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Timer as TimerIcon,
  Speed as SpeedIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  MedicalServices as MedicalIcon,
  Update as UpdateIcon
} from '@mui/icons-material';

interface EmergencyDispatch {
  id: string;
  patientName: string;
  age: number;
  location: {
    address: string;
    coordinates: { lat: number; lng: number };
  };
  destination: {
    hospital: string;
    address: string;
    coordinates: { lat: number; lng: number };
  };
  severity: 'stroke_suspected' | 'cardiac_event' | 'general_emergency';
  triggerTime: string;
  status: 'dispatched' | 'en_route_to_patient' | 'with_patient' | 'en_route_to_hospital' | 'arrived_hospital';
  crew: {
    paramedic: string;
    driver: string;
    ambulanceId: string;
  };
  vitals: {
    bloodPressure: string;
    heartRate: number;
    oxygenSaturation: number;
    consciousness: string;
  };
  treatment: {
    oxygenAdministered: boolean;
    ivAccess: boolean;
    medicationsGiven: string[];
    ecgPerformed: boolean;
  };
  familyContact: string;
  familyPhone: string;
  estimatedArrival: {
    toPatient: string;
    toHospital: string;
  };
  distance: {
    toPatient: string;
    toHospital: string;
  };
}

const AmbulanceDashboard: React.FC = () => {
  const [activeDispatch, setActiveDispatch] = useState<EmergencyDispatch>({
    id: 'AMB001',
    patientName: 'Juan Pérez',
    age: 68,
    location: {
      address: 'Av. Libertad 245, Chillán Centro',
      coordinates: { lat: -36.6067, lng: -72.1028 }
    },
    destination: {
      hospital: 'Hospital Herminda Martín',
      address: 'Av. Manuel Montt 115, Chillán',
      coordinates: { lat: -36.6089, lng: -72.1034 }
    },
    severity: 'stroke_suspected',
    triggerTime: new Date(Date.now() - 12 * 60 * 1000).toISOString(), // 12 minutes ago
    status: 'with_patient',
    crew: {
      paramedic: 'Carlos Mendoza',
      driver: 'Ana Rojas',
      ambulanceId: 'AMB-07'
    },
    vitals: {
      bloodPressure: '160/95',
      heartRate: 88,
      oxygenSaturation: 94,
      consciousness: 'Confuso, responde a estímulos'
    },
    treatment: {
      oxygenAdministered: true,
      ivAccess: true,
      medicationsGiven: ['Aspirina 300mg'],
      ecgPerformed: true
    },
    familyContact: 'María González (Hija)',
    familyPhone: '+56912345678',
    estimatedArrival: {
      toPatient: 'Llegamos',
      toHospital: '8 minutos'
    },
    distance: {
      toPatient: '0 km',
      toHospital: '2.3 km'
    }
  });

  const [patientUpdate, setPatientUpdate] = useState({
    vitals: { ...activeDispatch.vitals },
    consciousness: activeDispatch.vitals.consciousness,
    treatment: { ...activeDispatch.treatment },
    notes: ''
  });

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'dispatched': return 'DESPACHADO';
      case 'en_route_to_patient': return 'EN RUTA AL PACIENTE';
      case 'with_patient': return 'CON EL PACIENTE';
      case 'en_route_to_hospital': return 'EN RUTA AL HOSPITAL';
      case 'arrived_hospital': return 'LLEGÓ AL HOSPITAL';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'dispatched': return 'info';
      case 'en_route_to_patient': return 'warning';
      case 'with_patient': return 'primary';
      case 'en_route_to_hospital': return 'warning';
      case 'arrived_hospital': return 'success';
      default: return 'default';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'stroke_suspected': return 'ACV SOSPECHOSO';
      case 'cardiac_event': return 'EVENTO CARDÍACO';
      case 'general_emergency': return 'EMERGENCIA GENERAL';
      default: return severity;
    }
  };

  const handleStatusUpdate = (newStatus: string) => {
    setActiveDispatch(prev => ({ ...prev, status: newStatus as any }));
  };

  const handleVitalsUpdate = () => {
    setActiveDispatch(prev => ({
      ...prev,
      vitals: patientUpdate.vitals,
      treatment: patientUpdate.treatment
    }));
  };

  const sendHospitalUpdate = () => {
    // In real app: send update to hospital
    alert('Actualización enviada al hospital');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <AmbulanceIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Panel de Ambulancia - {activeDispatch.crew.ambulanceId}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Chip 
            icon={<EmergencyIcon />} 
            label={getSeverityLabel(activeDispatch.severity)} 
            color="error" 
          />
          <Chip 
            icon={<InfoIcon />} 
            label={getStatusLabel(activeDispatch.status)} 
            color={getStatusColor(activeDispatch.status) as any} 
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Emergency Chronometer & Patient Info */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3, border: '2px solid #f44336' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'error.main', mr: 2, width: 56, height: 56 }}>
                  {activeDispatch.patientName.split(' ').map(n => n[0]).join('')}
                </Avatar>
                <Box>
                  <Typography variant="h6">{activeDispatch.patientName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activeDispatch.age} años • {activeDispatch.id}
                  </Typography>
                </Box>
              </Box>

              <EmergencyChronometer 
                startTime={activeDispatch.triggerTime}
                variant="detailed"
                size="medium"
              />

              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {getSeverityLabel(activeDispatch.severity)}
                </Typography>
                <Typography variant="body2">
                  Prioridad máxima - Transporte inmediato requerido
                </Typography>
              </Alert>
            </CardContent>
          </Card>

          {/* Location & Route Info */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              <NavigationIcon sx={{ mr: 1 }} />
              Información de Ruta
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon><LocationIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Origen (Paciente)"
                  secondary={activeDispatch.location.address}
                />
                <Typography variant="body2" color="text.secondary">
                  {activeDispatch.estimatedArrival.toPatient}
                </Typography>
              </ListItem>
              
              <ListItem>
                <ListItemIcon><HospitalIcon color="error" /></ListItemIcon>
                <ListItemText 
                  primary="Destino"
                  secondary={`${activeDispatch.destination.hospital} - ${activeDispatch.destination.address}`}
                />
                <Typography variant="body2" color="text.secondary">
                  ETA: {activeDispatch.estimatedArrival.toHospital}
                </Typography>
              </ListItem>
            </List>

            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<NavigationIcon />}
                onClick={() => window.open(`https://maps.google.com/?q=${activeDispatch.destination.coordinates.lat},${activeDispatch.destination.coordinates.lng}`)}
              >
                Abrir GPS
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhoneIcon />}
                onClick={() => window.open(`tel:${activeDispatch.familyPhone}`)}
              >
                Llamar Familia
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Patient Status & Treatment */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <MedicalIcon sx={{ mr: 1 }} />
              Estado del Paciente
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Presión Arterial"
                  value={patientUpdate.vitals.bloodPressure}
                  onChange={(e) => setPatientUpdate(prev => ({
                    ...prev,
                    vitals: { ...prev.vitals, bloodPressure: e.target.value }
                  }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Frecuencia Cardíaca"
                  type="number"
                  value={patientUpdate.vitals.heartRate}
                  onChange={(e) => setPatientUpdate(prev => ({
                    ...prev,
                    vitals: { ...prev.vitals, heartRate: parseInt(e.target.value) }
                  }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Saturación O2 (%)"
                  type="number"
                  value={patientUpdate.vitals.oxygenSaturation}
                  onChange={(e) => setPatientUpdate(prev => ({
                    ...prev,
                    vitals: { ...prev.vitals, oxygenSaturation: parseInt(e.target.value) }
                  }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Estado de Conciencia</InputLabel>
                  <Select
                    value={patientUpdate.consciousness}
                    onChange={(e) => setPatientUpdate(prev => ({
                      ...prev,
                      consciousness: e.target.value
                    }))}
                    label="Estado de Conciencia"
                  >
                    <MenuItem value="Alerta y orientado">Alerta y orientado</MenuItem>
                    <MenuItem value="Confuso, responde a estímulos">Confuso, responde a estímulos</MenuItem>
                    <MenuItem value="Solo responde al dolor">Solo responde al dolor</MenuItem>
                    <MenuItem value="No responde">No responde</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }} fontWeight="bold">
              Tratamiento Administrado:
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {patientUpdate.treatment.oxygenAdministered && (
                <Chip label="Oxígeno" color="success" size="small" />
              )}
              {patientUpdate.treatment.ivAccess && (
                <Chip label="Acceso IV" color="success" size="small" />
              )}
              {patientUpdate.treatment.ecgPerformed && (
                <Chip label="ECG Realizado" color="success" size="small" />
              )}
              {patientUpdate.treatment.medicationsGiven.map((med, index) => (
                <Chip key={index} label={med} color="primary" size="small" />
              ))}
            </Box>

            <TextField
              label="Notas adicionales"
              multiline
              rows={3}
              fullWidth
              value={patientUpdate.notes}
              onChange={(e) => setPatientUpdate(prev => ({ ...prev, notes: e.target.value }))}
              size="small"
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<UpdateIcon />}
                onClick={handleVitalsUpdate}
              >
                Actualizar Vitales
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<HospitalIcon />}
                onClick={sendHospitalUpdate}
              >
                Notificar Hospital
              </Button>
            </Box>
          </Paper>

          {/* Status Update */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              <AssignmentIcon sx={{ mr: 1 }} />
              Actualizar Estado
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Estado Actual</InputLabel>
              <Select
                value={activeDispatch.status}
                onChange={(e) => handleStatusUpdate(e.target.value)}
                label="Estado Actual"
              >
                <MenuItem value="dispatched">Despachado</MenuItem>
                <MenuItem value="en_route_to_patient">En ruta al paciente</MenuItem>
                <MenuItem value="with_patient">Con el paciente</MenuItem>
                <MenuItem value="en_route_to_hospital">En ruta al hospital</MenuItem>
                <MenuItem value="arrived_hospital">Llegó al hospital</MenuItem>
              </Select>
            </FormControl>

            {activeDispatch.status === 'en_route_to_hospital' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography fontWeight="bold">
                  🚨 EN RUTA AL HOSPITAL
                </Typography>
                <Typography variant="body2">
                  ETA: {activeDispatch.estimatedArrival.toHospital} • El hospital ha sido notificado
                </Typography>
              </Alert>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Equipo de Ambulancia:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText 
                  primary="Paramédico"
                  secondary={activeDispatch.crew.paramedic}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText 
                  primary="Conductor"
                  secondary={activeDispatch.crew.driver}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Family Contact */}
        <Grid item xs={12}>
          <Alert severity="info">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  Contacto Familiar: {activeDispatch.familyContact}
                </Typography>
                <Typography variant="body2">
                  Teléfono: {activeDispatch.familyPhone} • Mantener informado del estado del paciente
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<PhoneIcon />}
                onClick={() => window.open(`tel:${activeDispatch.familyPhone}`)}
              >
                Llamar
              </Button>
            </Box>
          </Alert>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AmbulanceDashboard;